import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api.service';
import { useDropzone } from 'react-dropzone';
import Swal from 'sweetalert2';
import { calculateTotal } from '../../libs/sum';
import { withRouter } from 'react-router-dom';

const PayDebt = props => {
  const [debts, setDebts] = useState([]);
  const [purchaseId, setPurchaseId] = useState(null);
  const [alias, setAlias] = useState('');
  const [userName, setUserName] = useState('');
  const [accounts, setAccounts] = useState([]);
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [totalPendingAmount, setTotalPendingAmount] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  // null mientras carga, 'NOT_FOUND' si el link no existe/expiró/ya se usó,
  // 'NETWORK' si fue un error de conexión/servidor (nada que ver con el link).
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    noKeyboard: true,
    noClick: true,
    // react-dropzone v14 requiere accept como objeto {mimeType: [extensiones]},
    // no como string. Incluye heic/heif (fotos de iPhone) porque el backend
    // ahora las convierte a JPEG automáticamente.
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif']
    },
    onDrop: acceptedFiles => {
      if (acceptedFiles.length === 0) return;
      const selectedFile = acceptedFiles[0];
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        Swal.fire({
          icon: 'warning',
          title: 'Imagen muy pesada',
          text:
            'La imagen supera los 10MB. Por favor comprimila o toma una foto con menor resolución.'
        });
        return;
      }
      handleSubmit(selectedFile);
    },
    onDropRejected: rejectedFiles => {
      const rejectedName =
        rejectedFiles && rejectedFiles[0] && rejectedFiles[0].file
          ? rejectedFiles[0].file.name || ''
          : '';
      const isHeic = /\.heic$|\.heif$/i.test(rejectedName);
      Swal.fire({
        icon: 'warning',
        title: 'Formato no válido',
        text: isHeic
          ? 'Tu foto está en formato HEIC (típico de iPhone). Ve a Ajustes > Cámara > Formatos y activa "Más compatible", o adjunta la foto como JPG/PNG.'
          : 'Solo se aceptan imágenes JPG o PNG.'
      });
    }
  });

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#161616';
  }, []);

  useEffect(() => {
    let isMounted = true;

    const getDebts = async () => {
      try {
        const { hash } = props.match.params;
        const data = await ApiService.getPayDebts(hash);

        if (!isMounted) return;

        if (!data || !Array.isArray(data.debts) || data.debts.length === 0) {
          // El backend respondió pero sin deudas -- el link ya no es
          // válido (expiró, ya se usó para pagar, etc.), no un error de red.
          setLoadError('NOT_FOUND');
          return;
        }

        setDebts(data.debts || []);
        setAccounts((data.user && data.user.bank_accounts) || []);
        setPurchaseId(data.idPurchase);
        setTotalPendingAmount(data.total_pending_amount);

        // Si viene el usuario, guardar nombre y apellido
        if (data.user) {
          setAlias(data.user.name || '');
          setUserName(data.user.last_name || '');
        }
      } catch (e) {
        if (!isMounted) return;
        const status = e && e.response && e.response.status;
        // 404 = el backend confirma que el link no existe/expiró. Cualquier
        // otro caso (timeout, 5xx, red caída) es transitorio, no un link
        // inválido -- no tiene sentido mandar a login en ninguno de los dos.
        setLoadError(status === 404 ? 'NOT_FOUND' : 'NETWORK');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    getDebts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async selectedFile => {
    if (!selectedFile || !purchaseId || isUploading) return;
    setIsUploading(true);

    const { hash } = props.match.params;

    const formData = new FormData();
    formData.append('purchase_id', purchaseId);
    formData.append('file', selectedFile);
    formData.append('hash', hash);

    try {
      await ApiService.uploadVoucherPayDebt(formData);
      // A partir de acá el voucher YA se subió correctamente -- si algo
      // falla de acá en adelante (el refresco de datos), no hay que
      // mostrarle al cliente "error al subir el archivo", porque no es
      // cierto y puede hacer que suba el comprobante dos veces.
      setIsPaid(true);
      setPurchaseId(null);

      try {
        const data = await ApiService.getPayDebts(hash);
        setDebts((data && data.debts) || []);
      } catch (refreshError) {
        // El voucher ya se subió bien, solo no se pudo refrescar la tabla.
        // No es un error para el cliente.
      }
    } catch (error) {
      // Antes de asumir que falló: en conexiones móviles inestables la
      // subida puede haber tenido éxito en el servidor pero la respuesta
      // nunca le llegó al cliente (así pasó con un caso real). Reconsultamos
      // el estado real -- si ya hay comprobante para esta compra, no hay
      // que mostrarle un error que lo empuje a subirlo de nuevo.
      try {
        const recheck = await ApiService.getPayDebts(hash);
        const recheckDebts = (recheck && recheck.debts) || [];
        const thisDebt = recheckDebts.find(d => d.id === purchaseId);
        if (!thisDebt || thisDebt.has_voucher) {
          setIsPaid(true);
          setPurchaseId(null);
          setDebts(recheckDebts);
          return;
        }
        setDebts(recheckDebts);
      } catch (recheckError) {
        // No se pudo reconsultar (la red sigue caída) -- seguimos abajo
        // con el mensaje de error normal.
      }

      let message = 'Ocurrió un error al subir el archivo.';
      if (
        error &&
        error.response &&
        error.response.data &&
        typeof error.response.data.message === 'string'
      ) {
        message = error.response.data.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message
      });
      // No se toca purchaseId acá -- si se limpia, el botón "Subir
      // Voucher" desaparece y el cliente queda sin forma de reintentar
      // sin recargar toda la página.
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = text => {
    navigator.clipboard
      .writeText(text)
      .then(() => alert('Número copiado al portapapeles'))
  };

  const offlineBanner = !isOnline && (
    <div
      style={{
        background: '#dc3545',
        color: '#fff',
        textAlign: 'center',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}
    >
      Sin conexión a internet. Verifica tu red antes de subir tu comprobante.
    </div>
  );

  if (isLoading) {
    return (
      <div
        className="cart-container"
        style={{ backgroundColor: '#161616 !important' }}
      >
        {offlineBanner}
        <section className="section-b-space">
          <div
            className="container padding-cls text-center"
            style={{ padding: '60px 20px', color: '#fff' }}
          >
            <p>Cargando tu información...</p>
          </div>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="cart-container"
        style={{ backgroundColor: '#161616 !important' }}
      >
        {offlineBanner}
        <section className="section-b-space">
          <div
            className="container padding-cls text-center"
            style={{ padding: '60px 20px', color: '#fff' }}
          >
            {loadError === 'NOT_FOUND' ? (
              <>
                <h4>Este link ya no está disponible</h4>
                <p>
                  Puede que ya hayas realizado el pago, o que el link haya
                  expirado. Si crees que esto es un error, contáctanos.
                </p>
              </>
            ) : (
              <>
                <h4>No pudimos cargar tu información</h4>
                <p>Revisa tu conexión e intenta de nuevo en unos minutos.</p>
              </>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className="cart-container"
      style={{ backgroundColor: '#161616 !important' }}
    >
      {offlineBanner}
      <section className="section-b-space">
        <div className="container padding-cls">
          <div className="row-home title-theme-welcome">
            <div className="title-welcome">
              <div className="image-left-welcome">
                <img src="/assets/images/EMOJI_TREBOL.png"></img>
              </div>
              <label style={{ textTransform: 'uppercase' }}>
                HOLA {alias || `${alias} ${userName}`.trim()}
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="col-md-8">
              <div className="checkout-form pb-3">
                Te recordamos que mantienes las siguientes compras pendientes de
                pago:
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {debts.map(debt => (
                    <tr key={debt.id}>
                      <td align="left" className="witoutborder">
                        {debt.date}
                      </td>
                      <td align="left" className="witoutborder" coslpan="2">
                        {calculateTotal(
                          parseFloat(debt.pending_amount).toFixed(2)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <table className="table">
                <tbody>
                  <tr>
                    <td>
                      {isPaid ? (
                        <span className="text-success">PAGADO</span>
                      ) : (
                        <span className="text-warning">Pendiente</span>
                      )}
                    </td>
                    <td align="left" colSpan="1">
                      <strong>Total:</strong>{' '}
                      <strong className="text-danger">
                        {calculateTotal(
                          parseFloat(totalPendingAmount).toFixed(2)
                        )}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>

              {isUploading && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '1rem'
                    }}
                  >
                    {/* Spinner SVG aquí */}
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="xMidYMid"
                      style={{ background: 'none', display: 'inline-block' }}
                    >
                      <g transform="rotate(0 50 50)">
                        <rect
                          x="47"
                          y="24"
                          rx="3"
                          ry="3"
                          width="6"
                          height="12"
                          fill="#f39c12"
                        >
                          <animate
                            attributeName="opacity"
                            from="1"
                            to="0"
                            dur="1s"
                            begin="0s"
                            repeatCount="indefinite"
                          />
                        </rect>
                      </g>
                      {Array.from({ length: 11 }).map((_, i) => {
                        const angle = (i + 1) * 30;
                        return (
                          <g key={i} transform={`rotate(${angle} 50 50)`}>
                            <rect
                              x="47"
                              y="24"
                              rx="3"
                              ry="3"
                              width="6"
                              height="12"
                              fill="#f39c12"
                            >
                              <animate
                                attributeName="opacity"
                                from="1"
                                to="0"
                                dur="1s"
                                begin={`${(i + 1) * 0.083}s`}
                                repeatCount="indefinite"
                              />
                            </rect>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>
              )}

              {purchaseId && (
                <div className="mt-3 mb-3 text-center">
                  <button
                    onClick={open}
                    className="btn btn-success"
                    disabled={isUploading}
                  >
                    {isUploading ? 'Subiendo...' : 'Subir Voucher'}
                  </button>
                </div>
              )}

              <div
                id="debt"
                className="upload-voucher-box mt-4 mb-4"
                style={{ position: 'absolute', left: '-9999px' }}
              >
                <div>
                  <div className="overlay-content options-overlay-content">
                    <div className="container">
                      <div>
                        <label className="logout-text1"></label>
                      </div>
                      <div className="row" style={{ display: 'block' }}>
                        <style>{`
                @keyframes fadeIn {
                  from {
                    opacity: 0;
                    transform: scale(0.95);
                  }
                  to {
                    opacity: 1;
                    transform: scale(1);
                  }
                }

                .dropzone-container {
                  position: relative;
                  padding: 2rem;
                  margin: 1rem 0;
                  text-align: center;
                  background: #000;
                  border-radius: 12px;
                  overflow: hidden;
                  animation: fadeIn 0.5s ease-in-out forwards;
                }

                .dropzone-border {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  pointer-events: none;
                  z-index: 0;
                }

                .dropzone-content {
                  position: relative;
                  z-index: 1;
                }
              `}</style>

                        <div className="dropzone-container" {...getRootProps()}>
                          <svg
                            className="dropzone-border"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                          >
                            <rect
                              x="2"
                              y="2"
                              width="96"
                              height="96"
                              fill="none"
                              strokeWidth="2"
                              strokeDasharray="10,5"
                            />
                          </svg>

                          <div className="dropzone-content">
                            <input
                              {...getInputProps()}
                              style={{
                                position: 'absolute',
                                left: '-9999px'
                              }}
                            />
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                color: '#000',
                                background: '#000'
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#f39c12"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="checkout-form pb-3">
                Para pagar copia la cuenta que vayas a utilizar:
              </div>

              <table className="table">
                <tbody>
                  {accounts.map((account, index) => (
                    <tr key={index}>
                      <td align="left">{account.bank_name}</td>
                      <td align="left">
                        <button
                          onClick={() => {
                            copyToClipboard(account.account_number);
                          }}
                          className="btn btn-sm btn-outline-success"
                        >
                          Copiar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default withRouter(PayDebt);
